import React from 'react';
import {
  ActionButton,
  Dropdown,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
} from 'equal-ds-ui';
import {
  Check,
  Edit3,
  XCircle,
  Ban,
  Unlock,
  MoreVertical,
  Key,
} from 'lucide-react';
import type { AdminUserPermissions, AdminUserTableRow } from '../../../../types/admin-users';
import {
  ACCOUNT_STATUS,
  DISABLED_STATUSES,
  normalizeStatus,
  PROTECTED_ROLES,
} from '../../../../constants/admin-users.constants';

/** Hoisted to module scope — stable reference, no re-creation per render */
const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

export interface AdminUserActionCellProps {
  row: AdminUserTableRow;
  originalRow: AdminUserTableRow;
  permissions: AdminUserPermissions;
  currentUserId?: string | null;
  onToggleStatus: (row: AdminUserTableRow) => void;
  onEdit: (row: AdminUserTableRow) => void;
  onResetPassword: (row: AdminUserTableRow) => void;
  onApprove: (row: AdminUserTableRow) => void;
  onReject: (row: AdminUserTableRow) => void;
}

export const AdminUserActionCell: React.FC<AdminUserActionCellProps> = ({
  row,
  originalRow,
  permissions,
  currentUserId,
  onToggleStatus,
  onEdit,
  onResetPassword,
  onApprove,
  onReject,
}) => {
  const normalizedStatus = normalizeStatus(row.status);
  const isPendingApproval = normalizedStatus === ACCOUNT_STATUS.PENDING_APPROVAL;

  // Check if this row is the logged-in user (prevent self-deactivation)
  const targetUserId = originalRow.raw?.user_profile?.user_id
    ?? originalRow.raw?.user_profile?.id
    ?? row.id;
  const isSelf = !!(currentUserId && String(targetUserId) === String(currentUserId));

  // For pending approval users, show Approve/Reject buttons (no three-dot)
  if (isPendingApproval) {
    return (
      <div className="flex items-center gap-2 justify-center" onClick={stopPropagation}>
        {permissions.canApproveUser && (
          <ActionButton
            intent="approve"
            size="sm"
            onClick={() => onApprove(originalRow)}
          >
            <Check className="w-3.5 h-3.5" />
            Approve
          </ActionButton>
        )}
        {permissions.canRejectUser && (
          <ActionButton
            intent="reject"
            size="sm"
            onClick={() => onReject(originalRow)}
          >
            <Ban className="w-3.5 h-3.5" />
            Reject
          </ActionButton>
        )}
      </div>
    );
  }

  // Determine inline status action button. Status is normalized (uppercase, trimmed)
  // so lowercase 'locked' (MFA lock path) and both disabled spellings are handled.
  let statusButton: React.ReactNode = null;

  if (permissions.canToggleStatus && !isSelf) {
    if (normalizedStatus === ACCOUNT_STATUS.ACTIVE) {
      statusButton = (
        <ActionButton
          intent="warning"
          size="sm"
          onClick={() => onToggleStatus(originalRow)}
        >
          <XCircle className="w-3.5 h-3.5" />
          De-Activate
        </ActionButton>
      );
    } else if (DISABLED_STATUSES.has(normalizedStatus)) {
      // INACTIVE (admin-disabled) or DEACTIVE (auto-lockout) — both re-activate the same way.
      statusButton = (
        <ActionButton
          intent="approve"
          size="sm"
          onClick={() => onToggleStatus(originalRow)}
        >
          <Check className="w-3.5 h-3.5" />
          Activate
        </ActionButton>
      );
    } else if (normalizedStatus === ACCOUNT_STATUS.LOCKED) {
      statusButton = (
        <ActionButton
          intent="approve"
          size="sm"
          onClick={() => onToggleStatus(originalRow)}
        >
          <Unlock className="w-3.5 h-3.5" />
          Unlock
        </ActionButton>
      );
    }
  }

  // Three-dot menu for Edit + Reset Password
  const hasEditPermission = permissions.canEditUser;
  const rawRoleName = (originalRow.raw?.user_profile?.roles?.role_name ?? '').toLowerCase();
  const isProtectedRole = PROTECTED_ROLES.has(rawRoleName);
  const hasResetPasswordPermission = permissions.canResetPassword && !isProtectedRole;
  const hasMenuItems = hasEditPermission || hasResetPasswordPermission;

  if (!statusButton && !hasMenuItems) {
    return <span className="text-xs text-text-secondary">--</span>;
  }

  return (
    <div className="flex items-center gap-2 justify-center" onClick={stopPropagation}>
      {statusButton}
      {hasMenuItems && (
        <Dropdown>
          <DropdownTrigger asChild>
            <button
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 shadow-sm"
              aria-label="More actions"
            >
              <MoreVertical className="w-5 h-5 text-gray-600" />
            </button>
          </DropdownTrigger>
          <DropdownContent
            zIndex={9999}
            align="end"
            className="min-w-[160px] p-1 bg-white rounded-lg shadow-lg border border-gray-200"
          >
            {hasEditPermission && (
              <DropdownItem
                onSelect={() => onEdit(originalRow)}
                className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md cursor-pointer transition-colors"
              >
                <Edit3 className="w-4 h-4 text-gray-700" />
                <span>Edit User</span>
              </DropdownItem>
            )}
            {hasResetPasswordPermission && (
              <DropdownItem
                onSelect={() => onResetPassword(originalRow)}
                className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md cursor-pointer transition-colors"
              >
                <Key className="w-4 h-4 text-gray-700" />
                <span>Reset Password</span>
              </DropdownItem>
            )}
          </DropdownContent>
        </Dropdown>
      )}
    </div>
  );
};
